import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Receipt } from 'lucide-react';
import { Button, Skeleton } from '@/components/ui';
import {
  useDepositList,
  useWithdrawalCancel,
  useWithdrawalList,
  getWithdrawalGetBalanceQueryKey,
  getWithdrawalListQueryKey,
} from '@/lib/endpoints';
import { DepositListItemDtoStatus } from '@/lib/models/depositListItemDtoStatus';
import { WithdrawalResponseDtoStatus } from '@/lib/models/withdrawalResponseDtoStatus';
import type { DepositListItemDto } from '@/lib/models/depositListItemDto';
import type { WithdrawalResponseDto } from '@/lib/models/withdrawalResponseDto';
import { formatCurrency } from '@/lib/format-currency';
import { cn } from '@/lib/utils';

/** Settled rows shown before the list is cut off — deeper history is off-page for now. */
const MAX_SETTLED_ROWS = 8;

/** One normalized statement row (deposits and withdrawals share the layout). */
interface ActivityEntry {
  key: string;
  kind: 'deposit' | 'withdrawal';
  label: string;
  date: Date;
  amountCents: number;
  currency: string;
  /** 'in' = credited (green +), 'out' = debited (−), 'void' = never moved (muted, unsigned). */
  amountTone: 'in' | 'out' | 'void';
  chip?: { label: string; className: string };
  /** Withdrawal id when the row is a cancellable pending withdrawal. */
  cancellableId?: string;
}

const CHIP = {
  pending: { label: 'Pending', className: 'bg-warning/10 text-warning' },
  processing: { label: 'Processing', className: 'bg-warning/10 text-warning' },
  failed: { label: 'Failed', className: 'bg-danger-alt/10 text-danger-alt' },
  rejected: { label: 'Rejected', className: 'bg-danger-alt/10 text-danger-alt' },
  cancelled: { label: 'Cancelled', className: 'bg-ink-muted/10 text-ink-muted' },
} as const;

/**
 * Wallet activity (the wallet page's Activity tab): a chrome-less statement
 * (no card, no border) with strict grid columns — description | amount |
 * action — so amounts and chips align across every row. Grouped into
 * "In flight" (pending withdrawals with inline cancel + processing deposits)
 * and "Earlier" (settled rows; chips only when something is abnormal).
 *
 * @param onDeposit When provided, the empty state offers a first-deposit CTA.
 */
export function WalletActivityList({ onDeposit }: { onDeposit?: () => void } = {}) {
  const queryClient = useQueryClient();
  const withdrawalsQuery = useWithdrawalList();
  const depositsQuery = useDepositList();

  const cancelMutation = useWithdrawalCancel({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getWithdrawalGetBalanceQueryKey() });
        queryClient.invalidateQueries({ queryKey: getWithdrawalListQueryKey() });
      },
    },
  });

  if (withdrawalsQuery.isLoading || depositsQuery.isLoading) {
    // Mirrors the statement rows (icon | two text lines | amount) so the list
    // doesn't morph from one flat block into rows when it resolves.
    return (
      <div>
        <Skeleton className="mb-1 h-3.5 w-16 rounded" />
        <ul>
          {Array.from({ length: 5 }).map((_, i) => (
            <li
              key={i}
              className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 border-b border-purple/10 py-3 last:border-b-0 lg:grid-cols-[minmax(0,1fr)_130px_88px] lg:gap-4"
            >
              <span className="flex min-w-0 items-center gap-3.5">
                <Skeleton className="size-9 shrink-0 rounded-full" />
                <span className="flex min-w-0 flex-col gap-1.5">
                  <Skeleton className="h-3.5 w-40 rounded" />
                  <Skeleton className="h-3 w-24 rounded" />
                </span>
              </span>
              <Skeleton className="h-3.5 w-16 justify-self-end rounded" />
              <span />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (withdrawalsQuery.isError && depositsQuery.isError) {
    return <p className="text-sm text-muted">Couldn't load your activity. Refresh the page to try again.</p>;
  }

  const withdrawals = withdrawalsQuery.data ?? [];
  const deposits = depositsQuery.data ?? [];

  const inFlight = [
    ...withdrawals.filter((w) => w.status === WithdrawalResponseDtoStatus.PENDING).map(withdrawalEntry),
    ...deposits.filter((d) => d.status === DepositListItemDtoStatus.PROCESSING).map(depositEntry),
  ].toSorted(byDateDesc);

  const settled = [
    ...withdrawals.filter((w) => w.status !== WithdrawalResponseDtoStatus.PENDING).map(withdrawalEntry),
    ...deposits.filter((d) => d.status !== DepositListItemDtoStatus.PROCESSING).map(depositEntry),
  ]
    .toSorted(byDateDesc)
    .slice(0, MAX_SETTLED_ROWS);

  if (inFlight.length === 0 && settled.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1.5 rounded-card border border-dashed border-border px-6 py-12 text-center">
        <span className="mb-2 flex size-14 items-center justify-center rounded-full bg-input-soft text-purple">
          <Receipt className="size-6" strokeWidth={1.75} aria-hidden="true" />
        </span>
        <p className="text-[16px] font-semibold text-primary">No activity yet</p>
        <p className="max-w-[360px] text-sm text-muted">
          Deposits and withdrawals will show up here, along with anything still in flight.
        </p>
        {onDeposit && (
          <Button size="md" className="mt-4 px-6" onClick={onDeposit}>
            Make your first deposit
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      {inFlight.length > 0 && (
        <>
          <GroupTag>In flight</GroupTag>
          <ul>
            {inFlight.map((entry) => {
              const isCancellingThis = cancelMutation.isPending && cancelMutation.variables?.id === entry.cancellableId;
              const errorOnThis = Boolean(cancelMutation.error) && cancelMutation.variables?.id === entry.cancellableId;
              return (
                <ActivityRow
                  key={entry.key}
                  entry={entry}
                  action={
                    entry.cancellableId ? (
                      <span className="flex items-center justify-end gap-2">
                        {errorOnThis && (
                          <span className="text-xs text-danger-alt">{extractError(cancelMutation.error)}</span>
                        )}
                        <button
                          type="button"
                          disabled={isCancellingThis}
                          onClick={() => cancelMutation.mutate({ id: entry.cancellableId! })}
                          className="text-[13px] font-semibold text-danger-alt transition-opacity hover:opacity-80 disabled:opacity-50"
                        >
                          {isCancellingThis ? 'Cancelling…' : 'Cancel'}
                        </button>
                      </span>
                    ) : null
                  }
                />
              );
            })}
          </ul>
        </>
      )}

      {settled.length > 0 && (
        <>
          <GroupTag>Earlier</GroupTag>
          <ul>
            {settled.map((entry) => (
              <ActivityRow key={entry.key} entry={entry} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function GroupTag({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-0.5 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted first:pt-0">
      {children}
    </p>
  );
}

/**
 * One statement row on a fixed grid — description (with the status chip
 * riding next to the title) | amount | action — so amounts align down the
 * whole list regardless of which cells a row fills.
 */
function ActivityRow({ entry, action }: { entry: ActivityEntry; action?: React.ReactNode }) {
  const isDeposit = entry.kind === 'deposit';
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 border-b border-purple/10 py-3 last:border-b-0 lg:grid-cols-[minmax(0,1fr)_130px_88px] lg:gap-4">
      <span className="flex min-w-0 items-center gap-3.5">
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-full',
            isDeposit ? 'bg-success/10 text-success-dark' : 'bg-input-soft text-purple',
          )}
        >
          {isDeposit ? (
            <ArrowDown className="size-4" aria-hidden="true" />
          ) : (
            <ArrowUp className="size-4" aria-hidden="true" />
          )}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-2">
            <span className="truncate text-[14px] capitalize text-primary">{entry.label}</span>
            {entry.chip && (
              <span className={cn('shrink-0 rounded-pill px-2.5 py-0.5 text-[11px] font-bold', entry.chip.className)}>
                {entry.chip.label}
              </span>
            )}
          </span>
          <span className="block text-xs text-muted">
            {entry.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </span>
      </span>
      <span
        className={cn(
          'text-right text-[14px] font-semibold tabular-nums',
          entry.amountTone === 'in' ? 'text-success-dark' : entry.amountTone === 'void' ? 'text-muted' : 'text-primary',
        )}
      >
        {entry.amountTone === 'in' ? '+' : entry.amountTone === 'out' ? '−' : ''}
        {formatCurrency(entry.amountCents, entry.currency)}
      </span>
      <span className="text-right">{action}</span>
    </li>
  );
}

function byDateDesc(a: ActivityEntry, b: ActivityEntry): number {
  return b.date.getTime() - a.date.getTime();
}

function withdrawalEntry(row: WithdrawalResponseDto): ActivityEntry {
  const chip =
    row.status === WithdrawalResponseDtoStatus.PENDING
      ? CHIP.pending
      : row.status === WithdrawalResponseDtoStatus.FAILED
        ? CHIP.failed
        : row.status === WithdrawalResponseDtoStatus.REJECTED
          ? CHIP.rejected
          : row.status === WithdrawalResponseDtoStatus.CANCELLED
            ? CHIP.cancelled
            : undefined;
  // Cancelled / rejected / failed withdrawals never left the wallet.
  const debited =
    row.status === WithdrawalResponseDtoStatus.PENDING ||
    row.status === WithdrawalResponseDtoStatus.APPROVED ||
    row.status === WithdrawalResponseDtoStatus.PROCESSING ||
    row.status === WithdrawalResponseDtoStatus.COMPLETED;
  return {
    key: `w_${row.id}`,
    kind: 'withdrawal',
    label: `Withdrawal · ${withdrawalMethodLabel(row)}`,
    date: new Date(row.createdDate),
    amountCents: row.amount,
    currency: row.currency,
    amountTone: debited ? 'out' : 'void',
    chip,
    cancellableId: row.status === WithdrawalResponseDtoStatus.PENDING ? row.id : undefined,
  };
}

function depositEntry(row: DepositListItemDto): ActivityEntry {
  const chip =
    row.status === DepositListItemDtoStatus.PROCESSING
      ? CHIP.processing
      : row.status === DepositListItemDtoStatus.FAILED
        ? CHIP.failed
        : undefined;
  // Prefer the charged card's brand + last4 (settle-webhook truth); fall
  // back to the coarse method type for older rows and non-card methods.
  const method = row.methodSummary
    ? `${row.methodSummary.brand} •••• ${row.methodSummary.last4}`
    : row.methodType === 'card'
      ? 'Card'
      : row.methodType === 'bank_account'
        ? 'Bank account'
        : null;
  return {
    key: `d_${row.id}`,
    kind: 'deposit',
    label: method ? `Deposit · ${method}` : 'Deposit',
    date: new Date(row.createdDate),
    amountCents: row.amount,
    currency: row.currency,
    // A failed deposit never credited the wallet — no green, no sign.
    amountTone: row.status === DepositListItemDtoStatus.FAILED ? 'void' : 'in',
    chip,
  };
}

/** "Visa •••• 1111" when the method summary is known, else the method code prettified ("bank_account" → "Bank Account"). */
function withdrawalMethodLabel(row: WithdrawalResponseDto): string {
  if (row.methodSummary) return `${row.methodSummary.brand} •••• ${row.methodSummary.last4}`;
  if (!row.methodCode) return 'Saved method';
  return row.methodCode.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { message?: string | string[] })?.message;
    const reason = Array.isArray(message) ? message[0] : message;
    if (reason) return reason;
  }
  return 'Could not cancel.';
}
